import { ChangeEvent, InputHTMLAttributes, ReactNode, memo, useCallback, useState } from "react";
import { clsx } from "clsx";


import cls from "./Input.module.scss";

type HTMLInputProps = Omit<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, "value" | "onChange">;

interface InputProps extends HTMLInputProps {
  className?: string;
  inputClassName?: string;
  dataTestId?: string;
  rows?: number;
  label?: string;
  error?: string;
  value?: string | number;
  onChange?: (value: string) => void;
  extraContent?: ReactNode;
}

export const Input = memo((props: InputProps) => {
  const {
    className,
    inputClassName,
    dataTestId,
    rows,
    label,
    error,
    value,
    onChange,
    type = "text",
    extraContent,
    ...otherProps
  } = props;
  const [isFocused, setIsFocused] = useState(false);

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const onChangeTextArea = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
  };

  const onChangeFocus = useCallback(
    (flag: boolean) => () => {
      setIsFocused(flag);
    },
    []
  );

  const mods = {
    [cls.isFocused]: isFocused,
    [cls.hasValue]: !!value,
    [cls.hasError]: !!error,
  };

  return (
    <div className={clsx(cls.InputWrapper, {}, [className])}>
      {extraContent}
      {label && (
        <div className={cls.Label}>
          <label htmlFor={label}>{label}</label>
        </div>
      )}
      {rows ? (
        <textarea
          id={label}
          value={value}
          rows={rows}
          data-testid={dataTestId}
          onChange={onChangeTextArea}
          onFocus={onChangeFocus(true)}
          onBlur={onChangeFocus(false)}
          className={clsx(cls.Input, mods, [cls.TextArea, inputClassName])}
          {...otherProps}
        />
      ) : (
        <input
          id={label}
          type={type}
          value={value}
          data-testid={dataTestId}
          onChange={onChangeInput}
          onFocus={onChangeFocus(true)}
          onBlur={onChangeFocus(false)}
          className={clsx(cls.Input, mods, [inputClassName])}
          {...otherProps}
        />
      )}
      {error && <div className={cls.ErrorText}>{error}</div>}
    </div>
  );
});
